import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Divider
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { CustomerProfile } from '../../services';
import { useTranslation } from 'react-i18next';

interface CustomerProfileDisplayProps {
  profile: CustomerProfile;
}

interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  emphasized?: boolean;
}

const InfoItem: React.FC<InfoItemProps> = ({ icon, label, value, emphasized = false }) => (
  <Box display="flex" alignItems="flex-start" gap={2}>
    <Box sx={{ mt: 0.5, color: 'text.secondary' }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {label}
      </Typography>
      <Typography 
        variant={emphasized ? "h6" : "body1"} 
        fontWeight={emphasized ? "bold" : "medium"}
        sx={{ wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const CustomerProfileDisplay: React.FC<CustomerProfileDisplayProps> = ({ profile }) => {
  const { t } = useTranslation();
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" color="primary" gutterBottom sx={{ mb: 3 }}>
          {t('profile.companyInfo')}
        </Typography>
        
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={4}>
          {/* Company Details */}
          <Box flex={1}>
            <Stack spacing={3}>
              <InfoItem
                icon={<BusinessIcon />}
                label={t('profile.companyName')}
                value={profile.company_name}
                emphasized
              />

              <InfoItem
                icon={<PersonIcon />}
                label={t('profile.contactPerson')}
                value={profile.contact_person || 'Not specified'}
              />

              <InfoItem
                icon={<PhoneIcon />}
                label={t('profile.phoneNumber')}
                value={profile.phone || 'Not specified'}
              />

              <InfoItem
                icon={<LocationIcon />}
                label={t('profile.companyAddress')}
                value={profile.address || 'Not specified'}
              />
            </Stack>
          </Box>

          {/* System Information */}
          <Box flex={1}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {t('profile.accountStatus')}
                </Typography>
                <Chip
                  icon={profile.is_approved ? <CheckCircleIcon /> : <WarningIcon />}
                  label={profile.is_approved ? 'Approved' : 'Pending Approval'}
                  color={profile.is_approved ? 'success' : 'warning'}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Box>
            </Stack>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Additional Information */}
        <Box>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            {t('profile.accountInformation')}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {t('profile.accountCreated')}
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {formatDate(profile.created_at)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CustomerProfileDisplay;